import { format } from 'date-fns'
import { BlockSessionContent } from 'store/states/lessons.state'
import { getLanguage } from 'helpers/date.helper'
import { getBrowserTimezone } from 'helpers/timezone.helper'

const SESSION_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
const SESSION_TIME_REGEX = /^([01]?\d|2[0-3]):([0-5]\d)$/

export type SessionBlockStatus =
  BlockSessionContent['sessions'][number]['status']

export type SessionActionState = 'comingSoonDisabled' | 'join' | 'finished'

export type SessionActionLabelKey =
  | 'buttons.comingSoon'
  | 'buttons.join'
  | 'buttons.finished'

export type SessionJoinErrorKey =
  | 'errors.generic'
  | 'errors.sessionNotLive'
  | 'errors.sessionEnded'
  | 'errors.forbidden'
  | 'errors.meetingsDisabled'

const partsToRecord = (
  parts: Intl.DateTimeFormatPart[]
): Record<string, string> =>
  Object.fromEntries(parts.map(({ type, value }) => [type, value]))

export const parseSessionStartInstant = (
  startDate: string,
  startTime: string,
  sessionTimezone: string
): Date | null => {
  if (
    !SESSION_DATE_REGEX.test(startDate) ||
    !SESSION_TIME_REGEX.test(startTime)
  ) {
    return null
  }
  if (!sessionTimezone) return null

  const [year, month, day] = startDate.split('-').map(Number)
  const [hour, minute] = startTime.split(':').map(Number)

  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0)
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: sessionTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = partsToRecord(formatter.formatToParts(new Date(utcMs)))
    const parsedHour = Number(parts.hour === '24' ? '0' : parts.hour)
    const parsedMs = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      parsedHour,
      Number(parts.minute),
      0
    )
    const targetMs = Date.UTC(year, month - 1, day, hour, minute, 0)
    const diff = targetMs - parsedMs
    if (diff === 0) break
    utcMs += diff
  }

  return new Date(utcMs)
}

const formatInstantDateInTimezone = (
  instant: Date,
  viewerTimezone: string
): string => {
  const parts = partsToRecord(
    new Intl.DateTimeFormat('en-US', {
      timeZone: viewerTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(instant)
  )

  const year = Number(parts.year)
  const month = Number(parts.month)
  const day = Number(parts.day)

  return format(new Date(year, month - 1, day), 'dd/MMM/yyyy', {
    locale: getLanguage(),
  })
    .replace(/\./g, '')
    .toLowerCase()
}

const formatInstantTimeInTimezone = (
  instant: Date,
  viewerTimezone: string
): string => {
  const parts = partsToRecord(
    new Intl.DateTimeFormat('en-US', {
      timeZone: viewerTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(instant)
  )

  const hour = parts.hour === '24' ? '00' : parts.hour
  return `${hour}:${parts.minute}`
}

export const formatSessionDateLabel = (
  startDate: string,
  startTime: string,
  sessionTimezone: string,
  viewerTimezone: string = getBrowserTimezone()
): string => {
  const instant = parseSessionStartInstant(
    startDate,
    startTime,
    sessionTimezone
  )
  if (!instant) return startDate

  return formatInstantDateInTimezone(instant, viewerTimezone)
}

export const formatSessionTimeLabel = (
  startDate: string,
  startTime: string,
  sessionTimezone: string,
  viewerTimezone: string = getBrowserTimezone()
): string => {
  const instant = parseSessionStartInstant(
    startDate,
    startTime,
    sessionTimezone
  )
  if (!instant) {
    const [hours, minutes] = startTime.split(':').map(Number)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return startTime
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`
  }

  return formatInstantTimeInTimezone(instant, viewerTimezone)
}

export const formatSessionDurationLabel = (duration: number): string => {
  if (!Number.isFinite(duration) || duration <= 0) return ''
  return String(Math.round(duration))
}

export const getSessionActionState = (
  session: BlockSessionContent['sessions'][number]
): SessionActionState => {
  if (session.status === 'ended') return 'finished'
  if (session.canJoin) return 'join'
  return 'comingSoonDisabled'
}

export const getSessionActionLabelKey = (
  actionState: SessionActionState
): SessionActionLabelKey => {
  if (actionState === 'join') return 'buttons.join'
  if (actionState === 'finished') return 'buttons.finished'
  return 'buttons.comingSoon'
}

export const getJoinErrorTranslationKey = (
  error: unknown
): SessionJoinErrorKey => {
  const code =
    (error as { response?: { data?: { code?: string } } })?.response?.data
      ?.code ?? ''

  switch (code) {
    case 'sessionNotLive':
    case 'session_not_live':
      return 'errors.sessionNotLive'
    case 'sessionEnded':
    case 'session_ended':
      return 'errors.sessionEnded'
    case 'forbidden':
      return 'errors.forbidden'
    case 'meetingsDisabledForTenant':
    case 'meetings_disabled_for_tenant':
      return 'errors.meetingsDisabled'
    default:
      return 'errors.generic'
  }
}

export const isJoinDisabled = (
  session: BlockSessionContent['sessions'][number]
): boolean => {
  return !session.meetingUrl || !session.canJoin
}

export const shouldShowJoinEarlyTooltip = (
  session: BlockSessionContent['sessions'][number]
): boolean => {
  return session.status === 'upcoming' && !session.canJoin
}
